import React, { useEffect } from 'react';
import {
  CheckCircleOutline,
  InputRounded,
  PlaceOutlined,
} from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';

import { LocalizationTypography } from './localization';

import {
  EndingRentalInstruction,
  HeaderSuccessEjectedInform,
} from '~components/molecules';
import { WELCOME_BACK_PAGE_PATH } from '~configs';
import { useNavigateInApp } from '~hooks';
import { useMainContext, useUserSubscriptionContext } from '~providers';
import { handleOpenApp } from '~utils';

export const EjectSuccess = () => {
  const { activeSubscription } = useUserSubscriptionContext();
  const { rentalQuery, rentalId, ejectedSubscriptionInfo } = useMainContext();
  const isSubscriptionRental =
    !!rentalQuery?.data?.subscription?.userSubscriptionId;
  const rentedAt = rentalQuery?.data?.rentedAt;
  // Not from the rent response: a pass's first period only gets its real end
  // time when the station confirms, which that response cannot know yet.
  const unreturnedFeeAppliesAt = isSubscriptionRental
    ? activeSubscription?.endAt
    : rentalQuery?.data?.unreturnedFeeAppliesAt;

  const navigate = useNavigateInApp();

  /**
   * Navigate effect
   */
  useEffect(() => {
    if (!rentalId) {
      navigate(WELCOME_BACK_PAGE_PATH);
    }
  }, [navigate, rentalId]);

  if (!rentalId) return null;

  const useAppInstruction = (
    <LocalizationTypography
      variant="Inter/Body-sm"
      color="#6B7280"
      textId="app.successPage.button.useTheApp"
      components={{
        tag1: (
          <Typography
            variant="Inter/Body-sm"
            component="a"
            color="primary"
            onClick={handleOpenApp}
            sx={{ textDecoration: 'underline' }}
          />
        ),
      }}
      defaultText="You can <tag1>use the app</tag1> to find one near you."
    />
  );

  return (
    <Stack display="grid" gap={2.5}>
      <HeaderSuccessEjectedInform
        rentedAt={rentedAt}
        unreturnedFeeAppliesAt={unreturnedFeeAppliesAt}
        chargersLeft={
          // From the rent response — the only source that already counts
          // this ejection. Reading the pass here showed the older count.
          isSubscriptionRental ? ejectedSubscriptionInfo?.swapsLeft : undefined
        }
      />
      <Stack
        alignItems="center"
        justifyContent="center"
        gap={2}
        sx={{ paddingRight: 2.5, paddingLeft: 3.5, paddingBottom: 3 }}
      >
        <LocalizationTypography
          variant="Poppins/Heading-4"
          color="primary"
          textId="app.successPage.text.howToEndRental"
          defaultText="How to end my rental?"
        />
        <Stack gap={1.5}>
          <EndingRentalInstruction
            Icon={PlaceOutlined}
            textId="app.successPage.text.findAnyChargeStation"
            defaultText="<tag1>Find</tag1> any chargeFUZE station."
            subInstruction={useAppInstruction}
          />
          <EndingRentalInstruction
            Icon={InputRounded}
            textId="app.successPage.text.returnTheCharger"
            defaultText="<tag1>Return the charger</tag1> by inserting it into any empty slot."
          />
          <EndingRentalInstruction
            Icon={CheckCircleOutline}
            textId="app.successPage.text.rentalEnds"
            defaultText="<tag1>Rental ends</tag1> automatically!"
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
